import * as THREE from 'three';
import { Reflector } from '$lib/mujoco/reflector';
import { mujocoModel, mujocoSimulation, mujocoState, mujocoBodies, mujocoLights, clearMujocoStores } from '$lib/stores/mujoco';
import { getPosition, getQuaternion } from '$lib/mujoco/transforms.js';
import { getJointName } from '$lib/mujoco/utils';

const MATERIAL_OVERRIDES = {
	metal: {
		metalness: 0.75,
		roughness: 0.40,
		reflectivity: 0.45,
		envMapIntensity: 1.2,
		// clearcoat: 0.35,
		// clearcoatRoughness: 0.1
		clearcoat: 0.1,
		clearcoatRoughness: 0.1
	},
	black: {
		metalness: 0.2,
		roughness: 0.6,
		envMapIntensity: 0.25,
		clearcoat: 0.05,
		clearcoatRoughness: 0.2
	},
	glow: {
		color: new THREE.Color(0.2, 0.4, 1.0),
		metalness: 0.05,
		roughness: 0.2,
		envMapIntensity: 0.0,
		clearcoat: 0.2,
		clearcoatRoughness: 0.05,
		emissive: new THREE.Color(0.4, 0.6, 1.0),
		emissiveIntensity: 1.0
	}
};

function decodeName(namesArray, start, textDecoder) {
	let end = start;
	while (end < namesArray.length && namesArray[end] !== 0) {
		end++;
	}
	return textDecoder.decode(namesArray.subarray(start, end));
}

function createGeometryForGeom(mujoco, type, size, model, geomIndex, meshes) {
	// Set the default geometry. In MuJoCo, this is a sphere.
	let geometry = new THREE.SphereGeometry(size[0] * 0.5);

	if (type == mujoco.mjtGeom.mjGEOM_PLANE.value) {
		// Special handling for plane later.
	} else if (type == mujoco.mjtGeom.mjGEOM_HFIELD.value) {
		// TODO: Implement this.
	} else if (type == mujoco.mjtGeom.mjGEOM_SPHERE.value) {
		geometry = new THREE.SphereGeometry(size[0]);
	} else if (type == mujoco.mjtGeom.mjGEOM_CAPSULE.value) {
		geometry = new THREE.CapsuleGeometry(size[0], size[1] * 2.0, 20, 20);
	} else if (type == mujoco.mjtGeom.mjGEOM_ELLIPSOID.value) {
		geometry = new THREE.SphereGeometry(1); // Stretch this below
	} else if (type == mujoco.mjtGeom.mjGEOM_CYLINDER.value) {
		geometry = new THREE.CylinderGeometry(size[0], size[0], size[1] * 2.0);
	} else if (type == mujoco.mjtGeom.mjGEOM_BOX.value) {
		geometry = new THREE.BoxGeometry(size[0] * 2.0, size[2] * 2.0, size[1] * 2.0);
	} else if (type == mujoco.mjtGeom.mjGEOM_MESH.value) {
		let meshID = model.geom_dataid[geomIndex];

		if (!(meshID in meshes)) {
			geometry = new THREE.BufferGeometry();

			let vertex_buffer = model.mesh_vert.subarray(
				model.mesh_vertadr[meshID] * 3,
				(model.mesh_vertadr[meshID] + model.mesh_vertnum[meshID]) * 3
			);
			for (let v = 0; v < vertex_buffer.length; v += 3) {
				let temp = vertex_buffer[v + 1];
				vertex_buffer[v + 1] = vertex_buffer[v + 2];
				vertex_buffer[v + 2] = -temp;
			}

			let normal_buffer = model.mesh_normal.subarray(
				model.mesh_vertadr[meshID] * 3,
				(model.mesh_vertadr[meshID] + model.mesh_vertnum[meshID]) * 3
			);
			for (let v = 0; v < normal_buffer.length; v += 3) {
				let temp = normal_buffer[v + 1];
				normal_buffer[v + 1] = normal_buffer[v + 2];
				normal_buffer[v + 2] = -temp;
			}

			let uv_buffer = model.mesh_texcoord.subarray(
				model.mesh_texcoordadr[meshID] * 2,
				(model.mesh_texcoordadr[meshID] + model.mesh_vertnum[meshID]) * 2
			);
			let triangle_buffer = model.mesh_face.subarray(
				model.mesh_faceadr[meshID] * 3,
				(model.mesh_faceadr[meshID] + model.mesh_facenum[meshID]) * 3
			);
			geometry.setAttribute('position', new THREE.BufferAttribute(vertex_buffer, 3));
			geometry.setAttribute('normal', new THREE.BufferAttribute(normal_buffer, 3));
			geometry.setAttribute('uv', new THREE.BufferAttribute(uv_buffer, 2));
			geometry.setIndex(Array.from(triangle_buffer));
			meshes[meshID] = geometry;
		} else {
			geometry = meshes[meshID];
		}
	}

	return geometry;
}

function createMaterialForGeom(model, geomIndex, textDecoder, namesArray) {
	let texture = undefined;
	let color = [
		model.geom_rgba[geomIndex * 4 + 0],
		model.geom_rgba[geomIndex * 4 + 1],
		model.geom_rgba[geomIndex * 4 + 2],
		model.geom_rgba[geomIndex * 4 + 3]
	];

	let matName = null;
	const matId = model.geom_matid[geomIndex];

	if (matId != -1) {
		matName = decodeName(namesArray, model.name_matadr[matId], textDecoder);
		color = [
			model.mat_rgba[matId * 4 + 0],
			model.mat_rgba[matId * 4 + 1],
			model.mat_rgba[matId * 4 + 2],
			model.mat_rgba[matId * 4 + 3]
		];

		// Construct Texture from model.tex_rgb
		texture = undefined;
		let texId = model.mat_texid[matId];
		if (texId != -1) {
			let width = model.tex_width[texId];
			let height = model.tex_height[texId];
			let offset = model.tex_adr[texId];
			let rgbArray = model.tex_rgb;
			let rgbaArray = new Uint8Array(width * height * 4);
			for (let p = 0; p < width * height; p++) {
				rgbaArray[p * 4 + 0] = rgbArray[offset + (p * 3 + 0)];
				rgbaArray[p * 4 + 1] = rgbArray[offset + (p * 3 + 1)];
				rgbaArray[p * 4 + 2] = rgbArray[offset + (p * 3 + 2)];
				rgbaArray[p * 4 + 3] = 1.0;
			}
			texture = new THREE.DataTexture(
				rgbaArray,
				width,
				height,
				THREE.RGBAFormat,
				THREE.UnsignedByteType
			);
			if (texId == 2) {
				texture.repeat = new THREE.Vector2(50, 50);
				texture.wrapS = THREE.RepeatWrapping;
				texture.wrapT = THREE.RepeatWrapping;
			} else {
				texture.repeat = new THREE.Vector2(1, 1);
				texture.wrapS = THREE.RepeatWrapping;
				texture.wrapT = THREE.RepeatWrapping;
			}

			texture.needsUpdate = true;
		}
	}

	const matParams = {
		color: new THREE.Color(color[0], color[1], color[2]),
		transparent: color[3] < 1.0,
		opacity: color[3],
		specularIntensity: matId != -1 ? model.mat_specular[matId] * 0.5 : 0.0,
		reflectivity: matId != -1 ? model.mat_reflectance[matId] : 0.05,
		roughness: matId != -1 ? 1.0 - model.mat_shininess[matId] : 0.5,
		metalness: matId != -1 ? 0.1 : 0.0,
		map: texture
	};

	if (matName && MATERIAL_OVERRIDES[matName]) {
		Object.assign(matParams, MATERIAL_OVERRIDES[matName]);
	}

	return new THREE.MeshPhysicalMaterial(matParams);
}

function ensureBodyExists(bodies, bodyId, model, textDecoder, names_array) {
	if (!(bodyId in bodies)) {
		bodies[bodyId] = new THREE.Group();

		let start_idx = model.name_bodyadr[bodyId];
		let end_idx = start_idx;
		while (end_idx < names_array.length && names_array[end_idx] !== 0) {
			end_idx++;
		}
		let name_buffer = names_array.subarray(start_idx, end_idx);
		bodies[bodyId].name = textDecoder.decode(name_buffer);

		bodies[bodyId].bodyID = bodyId;
		bodies[bodyId].has_custom_mesh = false;
	}
}

function setupLights(model, bodies, mujocoRoot) {
	const lights = [];

	// Parse lights.
	for (let l = 0; l < model.nlight; l++) {
		let light = new THREE.SpotLight();
		if (model.light_directional[l]) {
			light = new THREE.DirectionalLight();
		} else {
			light = new THREE.SpotLight();
		}
		light.decay = model.light_attenuation[l] * 100;
		light.penumbra = 0.5;
		light.castShadow = true;

		light.shadow.mapSize.width = 1024;
		light.shadow.mapSize.height = 1024;
		light.shadow.camera.near = 1;
		light.shadow.camera.far = 10;

		if (bodies[0]) {
			bodies[0].add(light);
		} else {
			mujocoRoot.add(light);
		}
		lights.push(light);
	}

	if (model.nlight == 0) {
		let light = new THREE.DirectionalLight();
		mujocoRoot.add(light);
	}

	return lights;
}

export async function reloadScene() {
	// Delete the old scene and load the new scene
	this.scene.remove(this.scene.getObjectByName('MuJoCo Root'));
	[this.model, this.state, this.simulation, this.bodies, this.lights] = await loadSceneFromURL(
		this.mujoco,
		this.params.scene,
		this
	);
	this.simulation.forward();
	for (let i = 0; i < this.updateGUICallbacks.length; i++) {
		this.updateGUICallbacks[i](this.model, this.simulation, this.params);
	}
}

/** Loads a scene for MuJoCo
 * @param {mujoco} mujoco This is a reference to the mujoco namespace object
 * @param {string} filename This is the name of the .xml file in the /working/ directory of the MuJoCo/Emscripten Virtual File System
 * @param {MuJoCoDemo} parent The three.js Scene Object to add the MuJoCo model elements to
 */
export async function loadSceneFromURL(mujoco, filename, parent) {
	// Free the old simulation.
	if (parent.simulation != null) {
		parent.simulation.free();
		parent.model = null;
		parent.state = null;
		parent.simulation = null;
		// Clear the stores as well
		clearMujocoStores();
	}

  // alert("here1")
	// Load in the state from XML.
	try {
		parent.model = mujoco.Model.load_from_xml('/working/' + filename);
		console.log('Mujoco model loaded:', parent.model);
		window.mujocoModel = parent.model;
		window.getJointName = getJointName;
	} catch (e) {
		console.error('Failed to load model from xml: ', e);
		alert('Failed to load model from xml: ' + e);
		return;
	}
	parent.state = new mujoco.State(parent.model);
	parent.simulation = new mujoco.Simulation(parent.model, parent.state);
  // alert("here2")

	let model = parent.model;
	let state = parent.state;
	let simulation = parent.simulation;

	// Decode the null-terminated string names.
	let textDecoder = new TextDecoder('utf-8');
	let names_array = new Uint8Array(model.names);
	let fullString = textDecoder.decode(model.names);
	let names = fullString.split(textDecoder.decode(new ArrayBuffer(1)));

	// Create the root object.
	let mujocoRoot = new THREE.Group();
	mujocoRoot.name = 'MuJoCo Root';
	parent.scene.add(mujocoRoot);

	/** @type {Object.<number, THREE.Group>} */
	let bodies = {};
	/** @type {Object.<number, THREE.BufferGeometry>} */
	let meshes = {};
	/** @type {THREE.Light[]} */
	let lights;

	// Default material definition.
	let material = new THREE.MeshPhysicalMaterial();
	material.color = new THREE.Color(1, 1, 1);

	// Loop through the MuJoCo geoms and recreate them in three.js.
	for (let g = 0; g < model.ngeom; g++) {
		// Only visualize geom groups up to 2 (same default behavior as simulate).
		if (!(model.geom_group[g] < 3)) {
			continue;
		}

		// Get the body ID and type of the geom.
		let b = model.geom_bodyid[g];
		let type = model.geom_type[g];
		let size = [model.geom_size[g * 3 + 0], model.geom_size[g * 3 + 1], model.geom_size[g * 3 + 2]];

		// Create the body if it doesn't exist.
		ensureBodyExists(bodies, b, model, textDecoder, names_array);

		// Create geometry for this geom
		let geometry = createGeometryForGeom(mujoco, type, size, model, g, meshes);

		// Mark custom mesh bodies
		if (type == mujoco.mjtGeom.mjGEOM_MESH.value) {
			bodies[b].has_custom_mesh = true;
		}

		// Create material for this geom
		material = createMaterialForGeom(model, g, textDecoder, names_array);

		let mesh = new THREE.Mesh();
		if (type == 0) {
			mesh = new Reflector(new THREE.PlaneGeometry(100, 100), {
				clipBias: 0.003,
				texture: material.map
			});
			mesh.rotateX(-Math.PI / 2);
		} else {
			mesh = new THREE.Mesh(geometry, material);
		}

		mesh.castShadow = g == 0 ? false : true;
		mesh.receiveShadow = type != 7;
		mesh.bodyID = b;
		bodies[b].add(mesh);
		getPosition(model.geom_pos, g, mesh.position);
		if (type != 0) {
			getQuaternion(model.geom_quat, g, mesh.quaternion);
		}
		if (type == 4) {
			mesh.scale.set(size[0], size[2], size[1]);
		} // Stretch the Ellipsoid
	}

	// Parse tendons.
	let tendonMat = new THREE.MeshPhongMaterial();
	tendonMat.color = new THREE.Color(0.8, 0.3, 0.3);
	mujocoRoot.cylinders = new THREE.InstancedMesh(
		new THREE.CylinderGeometry(1, 1, 1),
		tendonMat,
		1023
	);
	mujocoRoot.cylinders.receiveShadow = true;
	mujocoRoot.cylinders.castShadow = true;
	mujocoRoot.add(mujocoRoot.cylinders);
	mujocoRoot.spheres = new THREE.InstancedMesh(
		new THREE.SphereGeometry(1, 10, 10),
		tendonMat,
		1023
	);
	mujocoRoot.spheres.receiveShadow = true;
	mujocoRoot.spheres.castShadow = true;
	mujocoRoot.add(mujocoRoot.spheres);

	// Setup lighting
	lights = setupLights(model, bodies, mujocoRoot);

	for (let b = 0; b < model.nbody; b++) {
		//let parent_body = model.body_parentid()[b];
		if (b == 0 || !bodies[0]) {
			mujocoRoot.add(bodies[b]);
		} else if (bodies[b]) {
			bodies[0].add(bodies[b]);
		} else {
			console.log('Body without Geometry detected; adding to bodies', b, bodies[b]);
			bodies[b] = new THREE.Group();
			bodies[b].name = names[b + 1];
			bodies[b].bodyID = b;
			bodies[b].has_custom_mesh = false;
			bodies[0].add(bodies[b]);
		}
	}

	parent.mujocoRoot = mujocoRoot;

	// Update the Svelte stores
	mujocoModel.set(model);
	mujocoSimulation.set(simulation);
	mujocoState.set(state);
	mujocoBodies.set(bodies);
	mujocoLights.set(lights);

	return [model, state, simulation, bodies, lights];
}

/** Downloads the scenes/examples folder to MuJoCo's virtual filesystem
 * @param {mujoco} mujoco */
export async function downloadExampleScenes(mujoco) {
	let allFiles = [
		'22_humanoids.xml',
		'adhesion.xml',
		// 'agility_cassie/assets/achilles-rod.obj',
		// 'agility_cassie/assets/cassie-texture.png',
		// 'agility_cassie/assets/foot-crank.obj',
		// 'agility_cassie/assets/foot.obj',
		// 'agility_cassie/assets/heel-spring.obj',
		// 'agility_cassie/assets/hip-pitch.obj',
		// 'agility_cassie/assets/hip-roll.obj',
		// 'agility_cassie/assets/hip-yaw.obj',
		// 'agility_cassie/assets/knee-spring.obj',
		// 'agility_cassie/assets/knee.obj',
		// 'agility_cassie/assets/pelvis.obj',
		// 'agility_cassie/assets/plantar-rod.obj',
		// 'agility_cassie/assets/shin.obj',
		// 'agility_cassie/assets/tarsus.obj',
		// 'agility_cassie/cassie.xml',
		// 'agility_cassie/scene.xml',
		'arm26.xml',
		'balloons.xml',
		'flag.xml',
		'hammock.xml',
		'humanoid.xml',
		'humanoid_body.xml',
		'mug.obj',
		'mug.png',
		'mug.xml',
		'scene.xml',
		// 'shadow_hand/assets/f_distal_pst.obj',
		// 'shadow_hand/assets/f_knuckle.obj',
		// 'shadow_hand/assets/f_middle.obj',
		// 'shadow_hand/assets/f_proximal.obj',
		// 'shadow_hand/assets/forearm_0.obj',
		// 'shadow_hand/assets/forearm_1.obj',
		// 'shadow_hand/assets/forearm_collision.obj',
		// 'shadow_hand/assets/lf_metacarpal.obj',
		// 'shadow_hand/assets/mounting_plate.obj',
		// 'shadow_hand/assets/palm.obj',
		// 'shadow_hand/assets/th_distal_pst.obj',
		// 'shadow_hand/assets/th_middle.obj',
		// 'shadow_hand/assets/th_proximal.obj',
		// 'shadow_hand/assets/wrist.obj',
		// 'shadow_hand/left_hand.xml',
		// 'shadow_hand/right_hand.xml',
		// 'shadow_hand/scene_left.xml',
		// 'shadow_hand/scene_right.xml',
		'simple.xml',
		'slider_crank.xml',
		'model_with_tendon.xml'
	];

	let requests = allFiles.map((url) => fetch('./examples/scenes/' + url));
	let responses = await Promise.all(requests);
	for (let i = 0; i < responses.length; i++) {
		let split = allFiles[i].split('/');
		let working = '/working/';
		for (let f = 0; f < split.length - 1; f++) {
			working += split[f];
			if (!mujoco.FS.analyzePath(working).exists) {
				mujoco.FS.mkdir(working);
			}
			working += '/';
		}
		console.log(mujoco.FS); // DEBUG

		if (
			allFiles[i].endsWith('.png') ||
			allFiles[i].toLowerCase().endsWith('.stl') ||
			allFiles[i].endsWith('.skn')
		) {
			mujoco.FS.writeFile(
				'/working/' + allFiles[i],
				new Uint8Array(await responses[i].arrayBuffer())
			);
		} else {
			mujoco.FS.writeFile('/working/' + allFiles[i], await responses[i].text());
		}
	}
}
